import React from 'react'
import { useSelector } from 'react-redux';
import { PortInfo, getStatusIcon, setObjectType } from '../../utils';
import { validateConnection } from '../../utils/DesignerUtils';
import NodeContent from './NodeContent';
import { Connection, Handle, NodeProps, Position } from 'reactflow';
import { WorkflowMode, WorkflowStatusModel } from '../../models/Workflow';
import { EventEmitter } from '../../utils/EventEmitter';
import { getUsedItemSelector } from '../../slices/designerSlice';
import { UsedItem } from '../../models/generics';
import { BdaDataset } from '../../models/BdaDataset';
import { BdaService } from '../../models/BdaService';

type ServiceNodeData = {
  inputs: PortInfo[];
  outputs: PortInfo[];
  mode: WorkflowMode;
  text: string;
  statusModel: WorkflowStatusModel;
  label: string;
}

export default function BatchServiceNode({ id, data, isConnectable }: NodeProps<ServiceNodeData>) {
  // const dispatch = useDispatch();
  const mode = data.mode.toLowerCase();
  const getUsedItem = useSelector(getUsedItemSelector);

  const isValidConnection = (connection: Connection): boolean => {
    const isValid = connection.source !== connection.target && validateConnection(connection.sourceHandle!, connection.targetHandle!);
    if (!isValid) return false;

    const sourceItem = getUsedItem(connection.source)!;

    if (sourceItem.type === 'dataset' && setObjectType<UsedItem<BdaDataset>>(sourceItem)) { // targetItem can only be the current service
      const input = data.inputs.find(input => input.id === connection.targetHandle)!;
      return (sourceItem.datasource!.type === 'messageBroker' && input.datasetMode === 'streaming') || (sourceItem.datasource!.type !== 'messageBroker' && input.datasetMode !== 'streaming');
    }
    else if ((sourceItem.type === 'batch' || sourceItem.type === 'stream') && setObjectType<UsedItem<BdaService>>(sourceItem)) { // TODO replace service type with just 'service' ???
      const targetItem = getUsedItem(connection.target) as UsedItem<BdaService>;
      const sourceOutput = (sourceItem.outputs as PortInfo[]).find(output => output.id === connection.sourceHandle);
      const targetInput = (targetItem.inputs as PortInfo[]).find(input => input.id === connection.targetHandle);
      return sourceOutput?.datasetMode === targetInput?.datasetMode;
    }

    return true;
  }
  const handlePortClick = (portId: string) => (evt: React.MouseEvent<HTMLDivElement>) => {
    evt.stopPropagation();

    const portInfo: PortInfo = {
      id: portId,
      nodeId: id,
      nodeName: data.text,
    };
    EventEmitter.dispatch('selected-port', portInfo);
  }

  return (
    <div className="service-node">
      {data.statusModel && <div className="service-status">{getStatusIcon(data.statusModel.status)}</div>}

      <div className="input-ports">
        {data.inputs.map(({ id, type, datasetMode }) => (
          <Handle
            className={`custom-handle service-handle ${type} ${datasetMode}`}
            key={id}
            type="target"
            position={Position.Top}
            id={id}
            isConnectable={isConnectable}
            isValidConnection={isValidConnection}
            onClick={handlePortClick(id)}
          />
        ))}
      </div>

      <NodeContent title={data.label} type={mode} />

      <div className="output-ports">
        {data.outputs.map(({ id, type, datasetMode }) => (
          <Handle
            type="source"
            key={id}
            className={`custom-handle service-handle ${type} ${datasetMode}`}
            position={Position.Bottom}
            id={id}
            isConnectable={isConnectable}
            isValidConnection={isValidConnection}
            onClick={handlePortClick(id)}
          />
        ))}
      </div>
    </div>
  );
}
